import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {api, downloadBlob} from '@/lib/api'
import {translateApiError} from '@/lib/errors'

function planMejoramientoUrl(kind: 'export.docx' | 'print', businessId: string, year: number): string {
  return `/businesses/${businessId}/sgsst/plan-mejoramiento/${kind}?year=${year}`
}

async function downloadWord(businessId: string, year: number): Promise<void> {
  await downloadBlob(planMejoramientoUrl('export.docx', businessId, year), 'plan-de-mejoramiento.docx')
}

/** A plain `window.open` on the API URL would not carry the Authorization header. */
async function openPrintView(businessId: string, year: number): Promise<void> {
  const resp = await api.get<string>(planMejoramientoUrl('print', businessId, year), {responseType: 'text'})
  const blob = new Blob([resp.data], {type: 'text/html'})
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener')
}

/** Sin librería de PDF en el backend: el PDF sale de la vista de impresión del navegador. */
export function PlanMejoramientoDownloadMenu({businessId, year}: {businessId: string; year: number}) {
  const {t} = useTranslation()
  const [pending, setPending] = useState<'word' | 'print' | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function handle(kind: 'word' | 'print') {
    setError(null)
    setPending(kind)
    try {
      if (kind === 'word') await downloadWord(businessId, year)
      else await openPrintView(businessId, year)
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* «Generando…» solo para Word: es la única salida que el servidor tarda segundos en hornear. */}
          <Button
            size="sm"
            variant="outline"
            disabled={pending !== null}
            aria-busy={pending === 'word'}
            data-testid="plan-download-button">
            {pending === 'word' ? t('plan.download.generating') : t('plan.download.button')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid="plan-download-word" onSelect={() => void handle('word')}>
            {t('plan.download.word')}
          </DropdownMenuItem>
          <DropdownMenuItem data-testid="plan-download-print" onSelect={() => void handle('print')}>
            {t('plan.download.print')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {error && (
        <p className="text-xs text-destructive" data-testid="plan-download-error">
          {error}
        </p>
      )}
    </div>
  )
}
